import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
  use,
} from "react";
import {
  ActionCodeSettings,
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithPopup,
  User,
  UserCredential,
  onIdTokenChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { setUserProperties } from "firebase/analytics";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "integrations/firebase/auth";
import { db } from "integrations/firebase/firestore";
import { analytics } from "integrations/firebase/app";
import { GlobalLoading } from "components/loading";
import { userDocumentSchema } from "models/user";
import { parseDocument } from "services/documentParsing";
import { getEmailDomain } from "shared/services/Analytics";
import { logError } from "shared/services/ErrorReporting";
import { jwtDecode } from "jwt-decode";

const OAUTH_PROVIDERS = {
  google: new GoogleAuthProvider(),
};

type OAuthProviderType = keyof typeof OAUTH_PROVIDERS;

interface AuthState {
  authUser?: User | null;
  loading: boolean;
  error?: Error;
  isLoggedIn: boolean;
  authToken?: string;
}

interface AuthActions {
  signInWithOauth: (type: OAuthProviderType) => Promise<UserCredential>;
  sendLoginLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

interface AuthMeta {
  initialized: boolean;
}

export interface AuthContextValue {
  state: AuthState;
  actions: AuthActions;
  meta: AuthMeta;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const context = use(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [authToken, setAuthToken] = useState<string>();
  const [initialized, setInitialized] = useState(false);
  const [authUser = null, loading, error] = useAuthState(auth);

  useEffect(() => {
    const setupUser = async (user: User) => {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        const { email, uid } = user;
        await setDoc(userDocRef, {
          userId: uid,
          email,
          createdAt: new Date(),
        });
        return;
      }
      parseDocument(userDocumentSchema, userDocSnap);
    };

    if (authUser) {
      setupUser(authUser).catch(logError);
    }
  }, [authUser]);

  const signOut = useCallback(() => auth.signOut(), []);

  useEffect(() => {
    if (loading) {
      return; // Still loading, wait.
    }

    // Auth state has been determined (loading is false)
    setInitialized(true); // Mark as initialized
  }, [authUser, loading]);

  const signInWithOauth = useCallback(async (type: OAuthProviderType) => {
    const provider = OAUTH_PROVIDERS[type];

    if (!provider) {
      throw new Error(`No provider found for type ${type}`);
    }

    return signInWithPopup(auth, provider);
  }, []);

  const sendLoginLink = useCallback(async (email: string) => {
    try {
      try {
        window.localStorage.setItem("emailForSignIn", email);
      } catch {
        // Storage may be unavailable in private browsing
      }

      const actionCodeSettings: ActionCodeSettings = {
        handleCodeInApp: true,
        url: `${window.location.origin}/login-link`,
      };
      const linkDomain = import.meta.env.VITE_FIREBASE_AUTH_LINK_DOMAIN?.trim();
      if (linkDomain) {
        actionCodeSettings.linkDomain = linkDomain;
      }

      return await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    } catch (e) {
      try {
        window.localStorage.removeItem("emailForSignIn");
      } catch {
        // Storage may be unavailable
      }
      throw e;
    }
  }, []);

  useEffect(() => {
    if (!authUser) {
      setAuthToken(undefined);
      return;
    }

    const updateToken = async (user: User, forceUpdate: boolean = false) => {
      try {
        const token = await user.getIdToken(forceUpdate);
        setAuthToken(token);
        return token;
      } catch (err) {
        logError(err);
        setAuthToken(undefined);
      }
    };

    updateToken(authUser);

    let refreshTimeout: NodeJS.Timeout;
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await updateToken(user, true);
        if (!token) return;

        const decodedToken = jwtDecode(token);
        if (!decodedToken.exp) {
          logError(new Error("Token decoding failed"));
          return;
        }
        const expirationTime = decodedToken.exp * 1000;
        const timeToRefresh = expirationTime - Date.now() - 5 * 60 * 1000;

        refreshTimeout = setTimeout(() => {
          updateToken(user, true);
        }, timeToRefresh);
      } else {
        setAuthToken(undefined);
      }
    });

    return () => {
      unsubscribe();
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [authUser]);

  useEffect(() => {
    if (!authUser?.email) {
      return;
    }

    try {
      const userGroup = getEmailDomain(authUser.email).includes("chaptr")
        ? "internal"
        : "external";

      setUserProperties(analytics, { user_group: userGroup });
    } catch (_error) {
      // Silently fail if analytics is not available
    }
  }, [authUser]);

  const isLoggedIn = !!authUser;
  const authProviderValue = useMemo(
    () => ({
      state: {
        authUser,
        loading,
        error,
        isLoggedIn,
        authToken,
      },
      actions: {
        signInWithOauth,
        signOut,
        sendLoginLink,
      },
      meta: {
        initialized,
      },
    }),
    [
      authUser,
      loading,
      error,
      isLoggedIn,
      authToken,
      signInWithOauth,
      signOut,
      sendLoginLink,
      initialized,
    ],
  );

  if (loading || !initialized) {
    return <GlobalLoading />;
  }

  return (
    <AuthContext.Provider value={authProviderValue}>
      {children}
    </AuthContext.Provider>
  );
};
